import type { DayWithLocations } from '../../types';
import type { SelfServiceApiContext } from './types';
import { runWithSpan } from './tracing';

interface TimeSlotResponse {
  days: DayWithLocations[];
}

const normalizeSlots = (data: any): TimeSlotResponse => {
  if (!data || !Array.isArray(data.days)) {
    return { days: [] };
  }

  const normalizedDays = data.days.map((day: any) => ({
    ...day,
    locations: Array.isArray(day.locations)
      ? day.locations.map((loc: any) => ({
          ...loc,
          times: Array.isArray(loc.availableTimes) ? loc.availableTimes : Array.isArray(loc.times) ? loc.times : []
        }))
      : []
  }));

  return { days: normalizedDays };
};

export const fetchTimeSlots = async (ctx: SelfServiceApiContext, days: number): Promise<TimeSlotResponse> => {
  return runWithSpan('api.fetchTimeSlots', async span => {
    span.setAttribute('times.days', days);
    await ctx.delay(ctx.config.delays.fetchTimeSlots);

    const response = await ctx.httpClient.getJSON(`${ctx.baseUrl}/selfservice/timeslots?serviceType=passport&days=${days}`, {
      maxRetries: 3,
      onRetry: (attempt: number) => console.log(`Retrying fetchTimeSlots... attempt ${attempt}`)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to load time slots: ${response.status} ${error}`);
    }

    const payload = await response.json();
    return normalizeSlots(payload);
  });
};

export const reserveTime = async (
  ctx: SelfServiceApiContext,
  sessionId: string | null,
  date: string,
  time: string,
  location: number
): Promise<{ ok: boolean }> => {
  return runWithSpan('api.reserveTime', async span => {
    span.setAttribute('session.id', sessionId ?? '');
    span.setAttribute('appointment.date', date);
    span.setAttribute('appointment.time', time);
    span.setAttribute('appointment.location', location);
    await ctx.delay(ctx.config.delays.reserveTime);

    const response = await ctx.httpClient.postJSON(
      `${ctx.baseUrl}/selfservice/${sessionId ?? 'session'}/appointment`,
      { date, time, location: String(location) },
      {
        maxRetries: 3,
        onRetry: (attempt: number) => console.log(`Retrying reserveTime... attempt ${attempt}`)
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to reserve appointment: ${response.status} ${error}`);
    }

    const data = await response.json();
    return { ok: Boolean(data.success) };
  });
};


